/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { DataTableFilterMeta } from 'primereact/datatable';
import React, { Dispatch, SetStateAction } from 'react';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { ButtonGroup } from 'primereact/buttongroup';
import { ColumnProps } from 'primereact/column';

export interface DataTableColumns extends ColumnProps {
  order?: number;
}

type ColorType = 'accepted' | 'running' | 'dismissed' | 'failed' | 'successful';

export type Severity = 'success' | 'info' | 'warning' | 'danger' | 'contrast' | 'secondary' | undefined;

// eslint-disable-next-line react-refresh/only-export-components
export const statusColors: { [key in ColorType]: string } = {
  dismissed: '#8693a3',
  accepted: '#69DEF6',
  running: '#3B8AFD',
  failed: '#DC3545',
  successful: '#1BAB80'
};

export interface TableHeaderProps {
  filters: DataTableFilterMeta;
  setFilters: (filters: DataTableFilterMeta) => void;
  globalFilterValue: string;
  getCapabilities?: {
    title: string;
    link: string;
    formatter?: (link: string) => Promise<string>;
  };
  setGlobalFilterValue: Dispatch<SetStateAction<string>>;
  onRefresh?: {
    severity?: Severity;
    command?: () => Promise<unknown>;
  };
  onClearFilters?: {
    severity?: Severity;
    command?: () => void;
  };
}
const TableHeaderComponent: React.FC<TableHeaderProps> = ({
  filters,
  setFilters,
  globalFilterValue,
  getCapabilities,
  setGlobalFilterValue,
  onRefresh,
  onClearFilters
}) => {
  const { t } = useTranslation();

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const _filters = { ...filters };
    /** @ts-expect-error accept error */
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  return (
    <div className="flex xl:justify-content-between lg:justify-content-between sm:justify-content-center my-2 flex-wrap">
      <div className="flex align-items-center flex-wrap sm:w-full xl:w-fit lg:w-fit">
        <IconField className="sm:mb-3 xl:mb-0 lg:mb-0" iconPosition="left">
          <InputIcon className="pi pi-search" />
          <InputText
            className="p-inputtext-sm"
            value={globalFilterValue ?? ''}
            onChange={onGlobalFilterChange}
            placeholder={t(['actions.search', 'search'])}
          />
        </IconField>
        <div className="sm:w-full xl:w-fit lg:w-fit">
          <ButtonGroup>
            {onClearFilters && (
              <Button
                icon="pi pi-filter-slash"
                iconPos="right"
                className="ml-3"
                size="small"
                severity={onClearFilters.severity ?? 'secondary'}
                label={t(['actions.clear', 'clear'])}
                onClick={onClearFilters.command}
              />
            )}
            {onRefresh && (
              <Button
                icon="pi pi-refresh"
                iconPos="right"
                className=""
                size="small"
                severity={onRefresh.severity ?? undefined}
                label={t(['actions.refresh', 'refresh'])}
                onClick={onRefresh.command}
              />
            )}
          </ButtonGroup>
        </div>
      </div>

      {getCapabilities && (
        <a
          className="p-button p-component no-underline p-button-outlined p-button-link p-button-sm p-button-secondary font-semibold font-normal text-color"
          href={getCapabilities.link}
          onClick={async (e) => {
            e.preventDefault();
            const href = getCapabilities.formatter
              ? await getCapabilities.formatter(getCapabilities.link)
              : getCapabilities.link;
            window.open(href, '_blank');
          }}
          target="__blank"
        >
          <span>{getCapabilities.title || 'OGC API Processes GetCapabilities'}</span>
          <i className="pi pi-external-link ml-2" />
        </a>
      )}
    </div>
  );
};

export const TableHeader = React.memo(TableHeaderComponent);
